import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { FiArrowRight, FiCheck, FiCopy, FiGift, FiPlay, FiSend, FiShare2, FiStar, FiUsers } from "react-icons/fi";
import toast from "react-hot-toast";
import { PageHeader, Skeleton, StateBlock } from "../../components/UI";
import { useLanguage } from "../../contexts/LanguageContext";
import { useFormat } from "../../hooks/useFormat";
import { referralLink, REF_MAX_PERCENT, REF_POINTS_PER_PERCENT } from "../../lib/referral";
import { Page, Field, Action, request } from "./shared";
import { PlayerAvatar, Num } from "./home/primitives";

type Invitee = { id: string; username: string; avatar?: string; matches: number };
type Info = {
  code: string;
  invited: number;
  matches: number;
  points: number;
  pointsPerPercent: number;
  maxPercent: number;
  invitees: Invitee[];
};

const Hero = styled.section`
  display: grid;
  gap: 20px;
  padding: 28px;
  border-radius: 20px;
  background:
    radial-gradient(120% 140% at 100% 0%, ${(p) => p.theme.colors.primary}26 0%, transparent 55%),
    ${(p) => p.theme.colors.surface};
  box-shadow: inset 0 0 0 1px ${(p) => p.theme.colors.primary}33;
  @media (max-width: 767px) {
    padding: 20px 16px;
  }
  .icon {
    width: 44px;
    height: 44px;
    display: grid;
    place-items: center;
    border-radius: 12px;
    background: ${(p) => p.theme.colors.primarySoft};
    color: ${(p) => p.theme.colors.primary};
  }
  h2 {
    font-size: 22px;
    line-height: 30px;
    letter-spacing: -0.02em;
    max-width: 28ch;
  }
  p {
    max-width: 62ch;
    font-size: 15px;
    line-height: 22px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
`;
const LinkRow = styled.div`
  display: grid;
  gap: 8px;
  label {
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  .row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  input {
    flex: 1 1 260px;
    font-variant-numeric: tabular-nums;
  }
  .code {
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
    b {
      color: ${(p) => p.theme.colors.text};
      letter-spacing: 0.12em;
      font-weight: 650;
    }
  }
`;
const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }
`;
const Tile = styled.div<{ $accent?: boolean }>`
  display: grid;
  gap: 4px;
  align-content: start;
  padding: 20px;
  border-radius: 16px;
  background: ${(p) => (p.$accent ? p.theme.colors.primarySoft : p.theme.colors.surface)};
  box-shadow: inset 0 0 0 1px ${(p) => (p.$accent ? p.theme.colors.primary + "55" : "transparent")};
  .head {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${(p) => p.theme.colors.textSecondary};
    svg {
      color: ${(p) => (p.$accent ? p.theme.colors.primary : p.theme.colors.textMuted)};
    }
  }
  .n {
    font-size: 40px;
    line-height: 48px;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: ${(p) => (p.$accent ? p.theme.colors.primary : p.theme.colors.text)};
  }
  .hint {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;
const Progress = styled.div`
  display: grid;
  gap: 8px;
  margin-top: 12px;
  .bar {
    height: 6px;
    border-radius: 3px;
    background: ${(p) => p.theme.colors.input};
    overflow: hidden;
    i {
      display: block;
      height: 100%;
      border-radius: 3px;
      background: ${(p) => p.theme.colors.primary};
      transition: width 0.4s ${(p) => p.theme.motion.ease};
    }
  }
  .txt {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
`;
const Cols = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  align-items: start;
  @media (max-width: 1000px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Surface = styled.section`
  display: grid;
  gap: 16px;
  align-content: start;
  min-width: 0;
  padding: 24px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  @media (max-width: 767px) {
    padding: 16px;
  }
  h2 {
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
  }
`;
const Steps = styled.ol`
  list-style: none;
  display: grid;
  gap: 16px;
  li {
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr);
    gap: 12px;
    align-items: start;
  }
  .ic {
    width: 36px;
    height: 36px;
    display: grid;
    place-items: center;
    border-radius: 10px;
    background: ${(p) => p.theme.colors.primarySoft};
    color: ${(p) => p.theme.colors.primary};
  }
  strong {
    display: block;
    font-size: 15px;
    line-height: 22px;
  }
  span {
    font-size: 14px;
    line-height: 20px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
`;
const List = styled.ul`
  list-style: none;
  display: grid;
  gap: 2px;
  li {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 12px;
    min-height: 56px;
    padding: 6px 8px;
    border-radius: 12px;
    transition: background ${(p) => p.theme.motion.fast};
    &:hover {
      background: ${(p) => p.theme.colors.raised};
    }
  }
  a {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 15px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.text};
    &:hover {
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }
  .m {
    font-size: 14px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
`;

export default function Referral() {
  const { t } = useLanguage();
  const f = useFormat();
  const [info, setInfo] = useState<Info | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const load = () => {
    setError("");
    request<Info>("/referral")
      .then(setInfo)
      .catch((e) => setError((e as Error).message));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const perPercent = info?.pointsPerPercent || REF_POINTS_PER_PERCENT;
  const maxPercent = info?.maxPercent || REF_MAX_PERCENT;
  const maxPoints = perPercent * maxPercent;
  const link = info ? referralLink(info.code) : "";
  const points = info?.points ?? 0;
  const pct = Math.min(Math.floor(points / perPercent), maxPercent);
  const rate = { n: maxPoints, pct: maxPercent };

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(t("ref.copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("ref.copy_failed"));
    }
  }
  const share = () => navigator.share({ title: "CS Hub", text: t("ref.share_text"), url: link }).catch(() => undefined);

  return (
    <Page>
      <PageHeader title={t("ref.title")} description={t("ref.sub")} />

      {error ? (
        <StateBlock kind="error" title={t("ref.error")} text={error} actionLabel={t("ref.retry")} onAction={load} />
      ) : (
        <>
          <Hero aria-labelledby="ref-hero">
            <span className="icon">
              <FiGift size={22} aria-hidden="true" />
            </span>
            <div style={{ display: "grid", gap: 8 }}>
              <h2 id="ref-hero">{t("ref.hero.title")}</h2>
              <p>{t("ref.hero.text", rate)}</p>
            </div>
            <LinkRow>
              <label htmlFor="ref-link">{t("ref.link")}</label>
              {info ? (
                <>
                  <div className="row">
                    <Field id="ref-link" readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
                    <Action type="button" $variant="primary" onClick={copy}>
                      {copied ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
                      {t("ref.copy")}
                    </Action>
                    {typeof navigator.share === "function" && (
                      <Action type="button" onClick={share}>
                        <FiShare2 aria-hidden="true" />
                        {t("ref.share")}
                      </Action>
                    )}
                  </div>
                  <span className="code">
                    {t("ref.code")} <b>{info.code}</b>
                  </span>
                </>
              ) : (
                <Skeleton $h={44} $r={12} />
              )}
            </LinkRow>
          </Hero>

          <Stats aria-label={t("ref.title")}>
            <Tile>
              <span className="head">
                <FiUsers aria-hidden="true" />
                {t("ref.invited")}
              </span>
              <Num className="n">{info ? f.number(info.invited, 0) : "—"}</Num>
              <span className="hint">{t("ref.invited_hint")}</span>
            </Tile>
            <Tile>
              <span className="head">
                <FiPlay aria-hidden="true" />
                {t("ref.matches")}
              </span>
              <Num className="n">{info ? f.number(info.matches, 0) : "—"}</Num>
              <span className="hint">{t("ref.matches_hint")}</span>
            </Tile>
            <Tile $accent>
              <span className="head">
                <FiStar aria-hidden="true" />
                {t("ref.points")}
              </span>
              <Num className="n">{info ? f.number(points, 0) : "—"}</Num>
              <span className="hint">{t("ref.points_hint")}</span>
              {info && (
                <Progress>
                  <div className="bar" role="progressbar" aria-valuemin={0} aria-valuemax={maxPoints} aria-valuenow={Math.min(points, maxPoints)} aria-label={t("ref.points")}>
                    <i style={{ width: `${Math.min(100, (points / maxPoints) * 100)}%` }} />
                  </div>
                  <span className="txt">
                    {points >= maxPoints ? t("ref.progress_full", { pct: maxPercent }) : t("ref.progress", { n: maxPoints - points, pct: maxPercent })}
                  </span>
                  <Action as={Link} to="/subscription" $variant={pct > 0 ? "primary" : "secondary"}>
                    {t("ref.use")}
                    <FiArrowRight aria-hidden="true" />
                  </Action>
                </Progress>
              )}
            </Tile>
          </Stats>

          <Cols>
            <Surface aria-labelledby="ref-how">
              <h2 id="ref-how">{t("ref.how")}</h2>
              <Steps>
                {[
                  { icon: FiSend, k: "step1" },
                  { icon: FiPlay, k: "step2" },
                  { icon: FiGift, k: "step3" },
                ].map((s) => (
                  <li key={s.k}>
                    <span className="ic">
                      <s.icon aria-hidden="true" />
                    </span>
                    <div>
                      <strong>{t(`ref.${s.k}.title`)}</strong>
                      <span>{t(`ref.${s.k}.text`, rate)}</span>
                    </div>
                  </li>
                ))}
              </Steps>
            </Surface>

            <Surface aria-labelledby="ref-list">
              <h2 id="ref-list">{t("ref.list")}</h2>
              {!info ? (
                <div role="status" aria-label={t("common.loading")} style={{ display: "grid", gap: 8 }}>
                  <Skeleton $h={48} $r={12} />
                  <Skeleton $h={48} $r={12} />
                </div>
              ) : info.invitees.length === 0 ? (
                <StateBlock kind="empty" title={t("ref.empty.title")} text={t("ref.empty.text")} />
              ) : (
                <List>
                  {info.invitees.map((p) => (
                    <li key={p.id}>
                      <PlayerAvatar name={p.username || "?"} src={p.avatar} size={36} />
                      <Link to={`/profile/${p.id}`}>{p.username || "—"}</Link>
                      <Num className="m">{t("ref.list_matches", { n: f.number(p.matches, 0) })}</Num>
                    </li>
                  ))}
                </List>
              )}
            </Surface>
          </Cols>
        </>
      )}
    </Page>
  );
}
